import {Button, Skeleton, Stack, Typography} from '@customink/pigment-react';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import useKlaviyoLoadedInfo from 'hooks/useKlaviyoLoadedInfo';
import getComingSoonFormId from 'utils/getComingSoonFormId';

const FORM_ID = getComingSoonFormId();

function FormContent({
  formRendered,
  formRequested,
  isKlaviyoLoaded,
  isLoadingKlaviyo,
  klaviyoLoadFailed,
  onFormRequest
}) {
  // Show loading skeleton while checking for Klaviyo and consent
  if (isLoadingKlaviyo) {
    return <Skeleton variant="rounded" width={220} height={45} />;
  }

  // If Klaviyo failed to load or consent was denied, show nothing
  if (klaviyoLoadFailed) {
    return null;
  }

  // Normal flow when Klaviyo is loaded and consent is given
  return (
    <>
      {!formRendered && isKlaviyoLoaded && (
        <Button onClick={onFormRequest} disabled={formRequested} size="large">
          {formRequested ? 'Loading form...' : 'Notify Me When Available'}
        </Button>
      )}
      {formRequested && isKlaviyoLoaded && (
        <div className={`klaviyo-form-${FORM_ID}`} />
      )}
    </>
  );
}

FormContent.propTypes = {
  formRendered: PropTypes.bool.isRequired,
  formRequested: PropTypes.bool.isRequired,
  isKlaviyoLoaded: PropTypes.bool.isRequired,
  isLoadingKlaviyo: PropTypes.bool.isRequired,
  klaviyoLoadFailed: PropTypes.bool.isRequired,
  onFormRequest: PropTypes.func.isRequired
};

export default function ComingSoonForm({colorCount}) {
  const [formRendered, setFormRendered] = useState(false);
  const [formRequested, setFormRequested] = useState(false);
  const {isKlaviyoLoaded, isLoadingKlaviyo, klaviyoLoadFailed} =
    useKlaviyoLoadedInfo();

  useEffect(() => {
    const handleKlaviyoForms = (e) => {
      if (e.detail?.type === 'embedOpen' && e.detail?.formId === FORM_ID) {
        setFormRendered(true);
      }
    };

    window.addEventListener('klaviyoForms', handleKlaviyoForms);

    return () => {
      window.removeEventListener('klaviyoForms', handleKlaviyoForms);
    };
  }, []);

  const handleFormRequest = () => {
    setFormRequested(true);
  };

  return (
    <Stack alignItems={formRendered ? undefined : 'flex-start'} gap="0.5rem">
      {colorCount > 1 && (
        <Typography
          sx={{
            color: 'rgba(0, 0, 0, 0.57)',
            fontSize: '0.825rem',
            fontWeight: 500,
            lineHeight: '125%',
            marginBottom: '0.5rem'
          }}>
          Imagery may not be available for all colors.
        </Typography>
      )}
      <Typography fontWeight={600} fontSize="1rem">
        Coming Soon!
      </Typography>
      <FormContent
        isLoadingKlaviyo={isLoadingKlaviyo}
        klaviyoLoadFailed={klaviyoLoadFailed}
        isKlaviyoLoaded={isKlaviyoLoaded}
        formRendered={formRendered}
        formRequested={formRequested}
        onFormRequest={handleFormRequest}
      />
    </Stack>
  );
}

ComingSoonForm.propTypes = {
  colorCount: PropTypes.number.isRequired
};
